const { CarouselElement } = window.BAO.Carousel
const { useArrows, useDots } = window.BAO.Carousel.plugins
const { getFocusableElements } = window.BAO.Utils

export class ZoomGallery extends CarouselElement {
  static get requiredElements () {
    return [...super.requiredElements, 'close', 'next', 'previous', 'thumbnail']
  }

  static get requiredExternalElements () {
    return ['toggle']
  }

  connectedCallback () {
    super.connectedCallback()

    this.close = this.close.bind(this)
    this.handleTabPress = this.handleTabPress.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.trackMouseMove = this.trackMouseMove.bind(this)

    /** @type {HTMLElement} */
    this.elementUsedToOpenGallery = null
  }

  setupListeners () {
    super.setupListeners()

    this.externalEls.toggle.elements.forEach(toggle => {
      this.listeners.add(toggle, 'click', () => {
        const index = Number(toggle.dataset.index)
        this.open(index)
      })
    })

    this.listeners.add(this.els.close.element, 'click', () => this.close())

    this.els.slide.elements.forEach(slide => {
      this.listeners.add(slide, 'click', () => this.handleSlideClick(slide))
    })

    this.listeners.add(this, 'click', event => {
      if (event.target === this) {
        this.close()
      }
    })
  }

  /** @param {number} activeIndex */
  open (activeIndex = 0) {
    this.elementUsedToOpenGallery = document.activeElement

    this.carousel.moveToIdx(activeIndex, false, { duration: 0 })

    this.setAttribute('open', '')

    window.requestAnimationFrame(() => {
      this.trapFocus()
    })
  }

  close () {
    this.removeAttribute('open')

    this.listeners.remove('keydown', this.handleKeydown)

    if (this.zoomingSlide) {
      this.setZoomingOnSlide(this.zoomingSlide, false)
    }

    this.elementUsedToOpenGallery?.focus()
    this.elementUsedToOpenGallery = null
  }

  /** @param {HTMLElement} slide */
  handleSlideClick (slide) {
    const shouldZoom =
      !('zooming' in slide.dataset) && window.matchMedia('(min-width: 901px)')

    this.setZoomingOnSlide(slide, shouldZoom)
  }

  /**
   * @param {HTMLElement} slide
   * @param {isZooming} boolean
   */
  setZoomingOnSlide (slide, isZooming) {
    if (isZooming) {
      slide.dataset.zooming = ''
      this.listeners.add(slide, 'mousemove', this.trackMouseMove)

      const removeZoomOnSlideChanged = () => {
        this.setZoomingOnSlide(slide, false)
        this.carousel.on('slideChanged', removeZoomOnSlideChanged, true)
      }

      this.carousel.on('slideChanged', removeZoomOnSlideChanged)
    } else {
      delete slide.dataset.zooming
      this.listeners.remove('mousemove', this.trackMouseMove)
    }
  }

  /** @param {MouseEvent} event */
  trackMouseMove (event) {
    const slide = event.currentTarget

    const x = (event.offsetX / slide.offsetWidth) * 100
    const y = (event.offsetY / slide.offsetHeight) * 100
    slide.style.setProperty('--ZoomGallerySlide_Translate', `${x}% ${y}%`)
  }

  trapFocus () {
    /**
     * @todo Find better way to approach this.
     * The button isn't focusable immediately
     * because visibility is set to hidden.
     */
    window.setTimeout(() => {
      this.focusableElements[0].focus()
    }, 200)

    this.listeners.add(document.body, 'keydown', this.handleKeydown)
  }

  /** @param {KeyboardEvent} event */
  handleKeydown (event) {
    /** @type {Record<string, (event: MouseEvent) => void} */
    const shortcuts = {
      Tab: this.handleTabPress,
      Escape: this.close,
    }

    const action = shortcuts[event.key]

    if (!action) {
      return
    }

    action(event)
  }

  /** @param {KeyboardEvent} event */
  handleTabPress (event) {
    const {
      0: first,
      [this.focusableElements.length - 1]: last,
    } = this.focusableElements

    const shouldLoopToFirstElement = !event.shiftKey && event.target === last
    const shouldLoopToLastElement = event.shiftKey && event.target === first
    const shouldOverrideDefault =
      shouldLoopToFirstElement || shouldLoopToLastElement

    if (shouldOverrideDefault) {
      event.preventDefault()
    }

    if (shouldLoopToFirstElement) {
      first.focus()
    } else if (shouldLoopToLastElement) {
      last.focus()
    }
  }

  /** @returns {KeenSliderOptions} */
  get options () {
    return {
      drag: false,
      loop: true,
      breakpoints: {
        '(min-width: 901px)': {
          drag: true,
        },
      },
    }
  }

  /** @returns {KeenSliderPlugins[]} */
  get plugins () {
    return [
      useArrows({
        next: this.els.next.element,
        previous: this.els.previous.element,
      }),
      useDots(this.els.thumbnail.elements),
    ]
  }

  get zoomingSlide () {
    return this.els.slide.elements.find(slide => 'zooming' in slide.dataset)
  }

  get focusableElements () {
    return getFocusableElements(this)
  }
}

if (!customElements.get('zoom-gallery')) {
  customElements.define('zoom-gallery', ZoomGallery)
}
